import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { operatorSurfaceClassName } from "../ui/OperatorSurface.tsx";

type WorkspaceCrumb = {
  label: string;
  href?: string | null;
};

export function WorkspaceHeader({
  eyebrow,
  title,
  description,
  breadcrumbs,
  badges,
  headingActions,
  actions,
  metadata,
  compact = false,
}: {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  breadcrumbs?: WorkspaceCrumb[];
  badges?: ReactNode;
  /** Primary page actions that belong beside the heading, not in a separate tray. */
  headingActions?: ReactNode;
  actions?: ReactNode;
  metadata?: ReactNode;
  /** Dense, data-first header for analytics and other inspection views. */
  compact?: boolean;
}) {
  return (
    <section className={operatorSurfaceClassName(compact ? "compact" : "default", true)}>
      {breadcrumbs?.length
        ? (
          <nav aria-label="Breadcrumb" className={compact ? "mb-2 font-data text-[11px] text-slate-500" : "mb-3 text-sm text-slate-500"}>
            <ol className="flex flex-wrap items-center gap-2">
              {breadcrumbs.map((crumb, index) => (
                <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-2">
                  {crumb.href
                    ? crumb.href.startsWith("/")
                      ? <Link to={crumb.href} className="hover:text-slate-800">{crumb.label}</Link>
                      : <a href={crumb.href} className="hover:text-slate-800">{crumb.label}</a>
                    : <span aria-current={index === breadcrumbs.length - 1 ? "page" : undefined} className="text-slate-800 [overflow-wrap:anywhere]">{crumb.label}</span>}
                  {index < breadcrumbs.length - 1 ? <span aria-hidden>/</span> : null}
                </li>
              ))}
            </ol>
          </nav>
        )
        : null}

      <div data-heading-row="true" className={compact ? "flex flex-wrap items-center justify-between gap-3" : "flex flex-wrap items-start justify-between gap-4"}>
        <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
          {!compact ? <p className="font-data text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">{eyebrow}</p> : null}
          <h1 className={compact ? "text-xl font-semibold tracking-[-0.02em] text-slate-950 [overflow-wrap:anywhere]" : "mt-2 text-3xl font-semibold tracking-[-0.04em] text-slate-950 [overflow-wrap:anywhere] md:text-4xl"}>
            {title}
          </h1>
          {description
            ? <div className={compact ? "mt-1 max-w-3xl text-xs leading-5 text-slate-500" : "mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-[15px]"}>{description}</div>
            : null}
        </div>
        {badges || headingActions ? (
          <div className="flex flex-wrap items-center gap-2">
            {badges}
            {headingActions ? <div data-heading-action="true">{headingActions}</div> : null}
          </div>
        ) : null}
      </div>

      {(actions || metadata)
        ? (
          <div className={compact ? "mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3" : "mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"}>
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              {metadata}
            </div>
            {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
          </div>
        )
        : null}
    </section>
  );
}
